import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
} from "react-native";
import React, { useState, useEffect } from "react";
import Colors from "../../../Resources/Color";
import { observer } from "mobx-react";
import Fonts from "../../../Resources/Fonts";
import GlobalStyles from "../../../Resources/Styles";
import { useTranslation } from "react-i18next";
import { useScreenDimensions, useDevice } from "../../_layout";
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  Feather,
} from "@expo/vector-icons";
import TracCard from "./TracCard";
import BlockCard from "./BlockCard";
import CompetitionCard from "./CompetitionCard";
import WorkoutsCard from "./WorkoutsCard";
import NotesCard from "./NotesCard";
import ChatCard from "./ChatCard";
import FormsCard from "./FormsCard";
import BodyweightCard from "./BodyweightCard";
import { startOfWeek, endOfWeek, formatDateRange } from "../../utils/dateUtils";
import MediaManager from "../../Classes/MediaManager";

const ClientSummary = observer(({ client }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const isNotSmall = screenSize !== "small";

  const [hoveredArrow, setHoveredArrow] = useState(null);
  const [isAddNoteHovered, setIsAddNoteHovered] = useState(false);

  // Check if there's any content in notes
  const hasPrivateNoteContent = client.notes && client.notes.trim().length > 0;
  const hasDailyNotesContent =
    client.notesArray &&
    client.notesArray.some((note) => {
      if (!note.date || !note.text || note.text.trim().length === 0)
        return false;
      const noteDate = new Date(note.date);
      const weekStart = client.summaryStartDate;
      if (!weekStart) return false;
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      return noteDate >= weekStart && noteDate < weekEnd;
    });
  const hasAnyNoteContent = hasPrivateNoteContent || hasDailyNotesContent;
  const shouldShowAddNoteButton = !hasAnyNoteContent;

  const [showNotesCard, setShowNotesCard] = useState(hasAnyNoteContent);

  // Initialize summary week dates to current week if not set
  useEffect(() => {
    if (!client.summaryStartDate) {
      const today = new Date();
      client.setProperty("summaryStartDate", startOfWeek(today));
    }
  }, []);

  // Check if we can go back (max 3 months)
  const canGoBack = () => {
    if (!client.summaryStartDate) return false;
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    return client.summaryStartDate > threeMonthsAgo;
  };

  const handlePreviousWeek = () => {
    if (!client.summaryStartDate || !canGoBack()) return;
    const newStart = new Date(client.summaryStartDate);
    newStart.setDate(newStart.getDate() - 7);
    client.setProperty("summaryStartDate", newStart);
  };

  const handleNextWeek = () => {
    if (!client.summaryStartDate) return;
    const newStart = new Date(client.summaryStartDate);
    newStart.setDate(newStart.getDate() + 7);
    client.setProperty("summaryStartDate", newStart);
  };

  const getWeekRangeLabel = () => {
    if (!client.summaryStartDate) {
      return "";
    }
    const language = i18n.language || "en";
    const summaryEndDate = endOfWeek(client.summaryStartDate);
    return formatDateRange(client.summaryStartDate, summaryEndDate, language);
  };

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.contentContainer,
          !isNotSmall && styles.contentContainerSmall,
          { paddingTop: isNotSmall ? 24 : 16 },
        ]}
      >
        {/* Header */}
        <View
          style={[
            styles.headerRow,
            !isNotSmall && styles.headerRowSmall,
            { marginBottom: isNotSmall ? 16 : 12 },
          ]}
        >
          <View style={styles.weekRangePicker}>
            <TouchableOpacity
              style={styles.navArrow}
              onMouseEnter={() =>
                !isMobile && canGoBack() && setHoveredArrow("back")
              }
              onMouseLeave={() => setHoveredArrow(null)}
              onPress={handlePreviousWeek}
              disabled={!canGoBack()}
            >
              <Ionicons
                name="chevron-back"
                size={18}
                color={
                  hoveredArrow === "back"
                    ? Colors.darkgrayColor
                    : Colors.grayColor
                }
                style={{ opacity: canGoBack() ? 1 : 0 }}
              />
            </TouchableOpacity>
            <Text style={styles.weekRangeLabel}>{getWeekRangeLabel()}</Text>
            <TouchableOpacity
              style={styles.navArrow}
              onMouseEnter={() => !isMobile && setHoveredArrow("forward")}
              onMouseLeave={() => setHoveredArrow(null)}
              onPress={handleNextWeek}
            >
              <Ionicons
                name="chevron-forward"
                size={18}
                color={
                  hoveredArrow === "forward"
                    ? Colors.darkgrayColor
                    : Colors.grayColor
                }
              />
            </TouchableOpacity>
          </View>

          {shouldShowAddNoteButton && (
            <TouchableOpacity
              style={styles.addNoteButton}
              onMouseEnter={() => !isMobile && setIsAddNoteHovered(true)}
              onMouseLeave={() => setIsAddNoteHovered(false)}
              onPress={() => setShowNotesCard(!showNotesCard)}
            >
              <Ionicons
                name="create-outline"
                size={16}
                color={
                  isAddNoteHovered ? Colors.darkgrayColor : Colors.grayColor
                }
              />
              <Text
                style={[
                  styles.addNoteText,
                  isAddNoteHovered && { color: Colors.darkgrayColor },
                ]}
              >
                {showNotesCard ? t("hideNote-string") : t("addNote-string")}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Main Grid */}
        <View
          style={[
            isNotSmall ? styles.mainGridTablet : styles.mainGrid,
            !isNotSmall && styles.mainGridSmall,
          ]}
        >
          {/* LEFT COLUMN */}
          <View
            style={[
              styles.column,
              isNotSmall && { flex: 1 },
              !isNotSmall && styles.columnSmall,
            ]}
          >
            {screenSize === "small" && (
              <NotesCard client={client} showNotesCard={showNotesCard} />
            )}
            <BlockCard client={client} />
            <CompetitionCard client={client} />
            <WorkoutsCard client={client} />
          </View>

          {/* RIGHT COLUMN */}
          <View
            style={[
              styles.column,
              isNotSmall && { flex: 1 },
              !isNotSmall && styles.columnSmall,
            ]}
          >
            {screenSize !== "small" && (
              <NotesCard client={client} showNotesCard={showNotesCard} />
            )}
            {!client.isOffline && <ChatCard client={client} />}

            {/* Forms and Bodyweight Row */}
            <View
              style={[
                styles.twoColumnRow,
                !isNotSmall && styles.twoColumnRowSmall,
              ]}
            >
              <FormsCard client={client} />
              <BodyweightCard client={client} />
            </View>

            <TracCard client={client} />
          </View>
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.backgroundColor,
  },
  contentContainer: {
    paddingTop: 24,
    maxWidth: 1400,
    alignSelf: "center",
    width: "100%",
  },
  contentContainerSmall: {
    paddingHorizontal: 0,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
  },
  headerRowSmall: {
    alignItems: "center",
  },
  addNoteButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 4,
  },
  addNoteText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  weekRangePicker: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  navArrow: {
    padding: 4,
  },
  weekRangeLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: Colors.blackColor,
    minWidth: 110,
    textAlign: "center",
  },
  mainGrid: {
    gap: 16,
    flexDirection: "column",
  },
  mainGridSmall: {
    gap: 12,
  },
  mainGridTablet: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  column: {
    gap: 16,
  },
  columnSmall: {
    gap: 12,
  },
  twoColumnRow: {
    flexDirection: "row",
    gap: 16,
    flexWrap: "wrap",
  },
  twoColumnRowSmall: {
    flexDirection: "column",
    gap: 12,
  },
  halfCard: {
    flex: 1,
    minWidth: 280,
  },
});

export default ClientSummary;
